// @givmo/org-console — the ONE picker of the organization's Collateral (Profile → Collateral, EMC-WEB11), rented
// by every surface that takes a graphic, a photo or a file from the library: the email designer's image swap
// (EMC-WEB10's hand editor, D-EMC-20 revised 2026-10-31), the Board Ask's attachments, a fundraiser's cover.
// One look, from the fundraiser cover's dialog (the KCC dev build, 2026-10-31): a grid of the library's items,
// each its picture and its title, chosen with a ring.
//
// * **What it offers**: the items in use (`status=current`), of the kinds asked for, and never one dated for a
//   day that has passed (the organization's own date, `today`, which every host must name from its time zone:
//   `organizationDate`): an item marked out of date, or last year's Giving Tuesday graphic, is not offered.
// * **Search and the tag filter** are the server's, as on Collateral, so a large library filters whole.
// * **One or several**: `multiple` chooses several (`selectedIds` starts the choice, the footer counts it);
//   without it, one.
// * **Upload a file** (`canUpload`, for a person who may change the library: Profile's own write) adds the
//   file to Collateral and chooses it, so a picture that isn't there yet is one step, never a trip to Profile.
// * **An order and a ceiling** (`ordered`, `max`; EMC-BE23, a campaign's share graphics): each chosen item
//   shows its place, the choice comes back in it, and once `max` are chosen no other can be (an upload then
//   joins Collateral unchosen).
// * **A choice outlasts the search that hid it**: every item offered since the dialog opened is remembered,
//   so what the current search doesn't show is still counted and still comes back.
//
// The library's bucket serves no browser a cross-origin read, so the bytes never pass through here: a host
// that needs the file copies it on the server (the cover's `…/fundraiser-cover`), or uses its `url`.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Button,
  Dialog,
  Icon,
  SearchInput,
  Select,
  Skeleton,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { Check, FileText, Upload } from 'lucide-react';
import {
  COLLATERAL_PAGE_LIMIT,
  addOrgCollateralItem,
  brandRefusalMessage,
  brandUploadRefusal,
  fetchOrgCollateral,
  orgCollateralKey,
  type CollateralItem,
  type CollateralKind,
} from './org-brand-wire';

export const COLLATERAL_PICKER_COPY = {
  title: 'Choose from Collateral',
  body: 'Photos and graphics from your organization’s Collateral.',
  choose: 'Use this',
  cancel: 'Cancel',
  search: 'Search by name or tag',
  allTags: 'All tags',
  tag: 'Tag',
  upload: 'Upload a file',
  uploading: 'Uploading…',
  uploadFailed: 'It wasn’t added. Please try again.',
  loadFailed: 'Collateral couldn’t be loaded. Please try again.',
  retry: 'Try again',
  more: 'Show more',
  empty: 'Nothing in Collateral to choose from yet.',
  noMatch: 'Nothing matches. Try another word or tag.',
  pdf: 'PDF',
  /** "1 file and 4 graphics selected". */
  /** "3 selected · up to 8", where the host sets a ceiling. */
  countOf: (count: number, max: number): string => `${count} selected · up to ${max}`,
  count: (files: number, graphics: number): string => {
    const parts: string[] = [];
    if (files > 0) parts.push(`${files} ${files === 1 ? 'file' : 'files'}`);
    if (graphics > 0) parts.push(`${graphics} ${graphics === 1 ? 'graphic' : 'graphics'}`);
    return parts.length === 0 ? 'Nothing selected' : `${parts.join(' and ')} selected`;
  },
};
const C = COLLATERAL_PICKER_COPY;
const ALL_TAGS = '__all';
const ACCEPT: Record<CollateralKind, string> = {
  image: 'image/png,image/jpeg',
  file: 'application/pdf',
};

/** The date, `YYYY-MM-DD`, in the organization's time zone: what the picker's `today` is. */
export function organizationDate(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/** Whether an item may be offered: in use, of a kind asked for, and not dated for a day gone by. */
export function collateralOffered(
  item: CollateralItem,
  kinds: readonly CollateralKind[],
  today: string,
): boolean {
  if (item.outOfDate || !kinds.includes(item.kind)) return false;
  return item.datedOn === null || item.datedOn >= today;
}

/** A search typed into the box reaches the server once the person pauses. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

function readKey(
  orgRef: string,
  kinds: readonly CollateralKind[],
  filter: { q: string; tag: string | null },
) {
  return [...orgCollateralKey(orgRef, filter), 'picker', [...kinds].sort().join('+')] as const;
}

async function readOffered(
  orgRef: string,
  kinds: readonly CollateralKind[],
  filter: { q: string; tag: string | null },
  signal?: AbortSignal,
  offset = 0,
) {
  const list = await fetchOrgCollateral(orgRef, {
    ...filter,
    offset,
    signal,
    currentOnly: true,
    kind: kinds.length === 1 ? kinds[0] : undefined,
  });
  return list;
}

/**
 * The library's items a picker would offer, unfiltered, or `null` while there are none to offer: dark here
 * (the route miss), not this person's to read, or nothing in use of these kinds. For a host deciding whether
 * to show its "Choose from Collateral" at all.
 */
export function useCollateralItems(
  orgRef: string | null,
  options: { kinds?: readonly CollateralKind[]; today: string },
): CollateralItem[] | null {
  const kinds = options.kinds ?? ['image'];
  const today = options.today;
  const read = useQuery({
    queryKey: readKey(orgRef ?? '', kinds, { q: '', tag: null }),
    enabled: orgRef !== null && orgRef.length > 0,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        return (await readOffered(orgRef as string, kinds, { q: '', tag: null }, signal)).items;
      } catch {
        return [];
      }
    },
  });
  const items = (read.data ?? []).filter((item) => collateralOffered(item, kinds, today));
  return items.length > 0 ? items : null;
}

/** The library's current images, or `null` where none can be offered. */
export function useCollateralImages(orgRef: string | null, today: string): CollateralItem[] | null {
  return useCollateralItems(orgRef, { kinds: ['image'], today });
}

export interface CollateralPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgRef: string;
  /** What it offers: images (photos and graphics), files (PDFs), or both. Images when unset. */
  kinds?: readonly CollateralKind[];
  /** Several at once, counted in the footer; one when unset. */
  multiple?: boolean;
  /** What starts chosen (a multiple picker's attachments so far, or the image a block shows now). */
  selectedIds?: readonly string[];
  /** The choice: one item, or (multiple) every item chosen, in the library's order. */
  onChoose: (items: CollateralItem[]) => void;
  title?: string;
  body?: string;
  confirmLabel?: string;
  /** Whether this person may add to the library (Profile's own write): offers "Upload a file". */
  canUpload?: boolean;
  /** The organization's own date, `YYYY-MM-DD` (`organizationDate`): an item dated before it is not offered. */
  today: string;
  /** Busy while the host applies the choice (a copy on the server). */
  applying?: boolean;
  /** A multiple picker's ceiling: once this many are chosen, no other item can be. */
  max?: number;
  /** The choice is an order: each item's mark sits first (top left, frame 20a's), a chosen one's showing its
   *  place, and `onChoose` gets them in it. */
  ordered?: boolean;
  /** Square pictures, whole (social graphics), where the default 16:9 suits a cover. */
  square?: boolean;
  /** Items the host already holds, its chosen ones, offered though not yet on a page read. */
  knownItems?: readonly CollateralItem[];
  testId?: string;
}

export function CollateralPickerDialog({
  open,
  onOpenChange,
  orgRef,
  kinds = ['image'],
  multiple = false,
  selectedIds = [],
  onChoose,
  title = C.title,
  body = C.body,
  confirmLabel = C.choose,
  canUpload = false,
  today,
  applying = false,
  max,
  ordered = false,
  square = false,
  knownItems = [],
  testId = 'collateral-picker',
}: CollateralPickerDialogProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const q = useSettled(search.trim(), 300);
  const [tag, setTag] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[]>([...selectedIds]);
  // Each open starts from what the host says is chosen now.
  const startedFrom = selectedIds.join(',');
  // Every item offered since this open: a choice outlasts the search that hid it.
  const [seen, setSeen] = useState<ReadonlyMap<string, CollateralItem>>(() => new Map());
  useEffect(() => {
    if (!open) return;
    setChosen(startedFrom.length === 0 ? [] : startedFrom.split(','));
    setSeen(new Map());
  }, [open, startedFrom]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // An item just uploaded, kept in view though a search or a tag would hide it.
  const [added, setAdded] = useState<CollateralItem[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const list = useInfiniteQuery({
    queryKey: [...readKey(orgRef, kinds, { q, tag }), 'pages'],
    enabled: open && orgRef.length > 0,
    retry: false,
    placeholderData: keepPreviousData,
    initialPageParam: 0,
    queryFn: ({ signal, pageParam }) => readOffered(orgRef, kinds, { q, tag }, signal, pageParam),
    // The server's pages, whole: the date is read here, so a page can offer fewer than it holds.
    getNextPageParam: (last, pages) => {
      const read = pages.reduce((sum, page) => sum + page.items.length, 0);
      return last.items.length === COLLATERAL_PAGE_LIMIT && read < last.total ? read : undefined;
    },
  });
  const items = useMemo(() => {
    const read = (list.data?.pages ?? [])
      .flatMap((page) => page.items)
      .filter((item) => collateralOffered(item, kinds, today));
    const fresh = added.filter((item) => !read.some((r) => r.id === item.id));
    const known = knownItems.filter(
      (item) =>
        collateralOffered(item, kinds, today) &&
        !read.some((r) => r.id === item.id) &&
        !fresh.some((f) => f.id === item.id),
    );
    return [...fresh, ...known, ...read];
  }, [list.data, added, knownItems, kinds, today]);
  useEffect(() => {
    setSeen((current) => {
      const fresh = items.filter((item) => current.get(item.id) !== item);
      return fresh.length === 0
        ? current
        : new Map([...current, ...fresh.map((item) => [item.id, item] as const)]);
    });
  }, [items]);
  const tags = list.data?.pages[0]?.tags ?? [];
  const rowOf = (id: string): CollateralItem[] => {
    const row = items.find((item) => item.id === id) ?? seen.get(id);
    return row === undefined ? [] : [row];
  };
  const chosenItems = ordered
    ? chosen.flatMap(rowOf)
    : [
        ...items.filter((item) => chosen.includes(item.id)),
        ...chosen.filter((id) => !items.some((item) => item.id === id)).flatMap(rowOf),
      ];
  const full = multiple && max !== undefined && chosenItems.length >= max;
  const filtering = q.length > 0 || tag !== null;

  const toggle = (id: string) =>
    setChosen((current) =>
      multiple
        ? current.includes(id)
          ? current.filter((c) => c !== id)
          : full
            ? current
            : [...current, id]
        : current.includes(id)
          ? []
          : [id],
    );

  const upload = async (file: File) => {
    setUploadError(null);
    const refusal = brandUploadRefusal(file, 'collateral');
    const kind: CollateralKind = file.type === 'application/pdf' ? 'file' : 'image';
    if (refusal !== null) {
      setUploadError(refusal);
      return;
    }
    if (!kinds.includes(kind)) {
      setUploadError(C.uploadFailed);
      return;
    }
    setUploading(true);
    try {
      const item = await addOrgCollateralItem(orgRef, file);
      setAdded((current) => [item, ...current]);
      setChosen((current) => {
        if (!multiple) return [item.id];
        // At the ceiling the file still joins Collateral, unchosen.
        return max !== undefined && current.flatMap(rowOf).length >= max
          ? current
          : [...current, item.id];
      });
      void queryClient.invalidateQueries({ queryKey: orgCollateralKey(orgRef) });
    } catch (error) {
      setUploadError(brandRefusalMessage(error, C.uploadFailed));
    } finally {
      setUploading(false);
    }
  };

  const close = (next: boolean) => {
    if (!next) {
      setSearch('');
      setTag(null);
      setUploadError(null);
    }
    onOpenChange(next);
  };

  const graphics = chosenItems.filter((item) => item.kind === 'image').length;
  const files = chosenItems.length - graphics;

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Content
        aria-label={title}
        width={680}
        className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0"
        testId={testId}
      >
        <div className="flex flex-col gap-6 px-24 pb-12 pt-24">
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {title}
          </Dialog.Title>
          <p className="font-sans text-s14 leading-h15 text-text-secondary">{body}</p>
        </div>
        <div className="flex flex-wrap items-center gap-8 px-24 pb-12">
          <SearchInput
            placeholder={C.search}
            aria-label={C.search}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClear={() => setSearch('')}
            containerClassName="w-full min-w-0 sm:w-auto sm:flex-1 sm:max-w-[280px]"
            testId={`${testId}-search`}
          />
          {tags.length > 0 && (
            <Select
              appearance="saas"
              aria-label={C.tag}
              value={tag ?? ALL_TAGS}
              options={[
                { value: ALL_TAGS, label: C.allTags },
                ...tags.map((t) => ({ value: t, label: t })),
              ]}
              onValueChange={(value) => setTag(value === null || value === ALL_TAGS ? null : value)}
              className="w-[160px]"
              testId={`${testId}-tag`}
            />
          )}
          {canUpload && (
            <>
              <Button
                appearance="saas"
                tone="secondary"
                className="ml-auto"
                leadingIcon={<Icon icon={Upload} size={15} aria-hidden />}
                loading={uploading}
                onClick={() => fileInput.current?.click()}
                testId={`${testId}-upload`}
              >
                {uploading ? C.uploading : C.upload}
              </Button>
              <input
                ref={fileInput}
                type="file"
                accept={kinds.map((kind) => ACCEPT[kind]).join(',')}
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (file !== undefined) void upload(file);
                }}
                {...testIdProp(`${testId}-upload-input`)}
              />
            </>
          )}
        </div>
        {uploadError !== null && (
          <p
            role="alert"
            className="mx-24 mb-12 rounded-md border border-danger-200 bg-danger-50 px-12 py-8 font-sans text-s13 text-danger-700"
            {...testIdProp(`${testId}-upload-error`)}
          >
            {uploadError}
          </p>
        )}
        <div
          className="min-h-[220px] flex-1 overflow-y-auto px-24 pb-16"
          {...testIdProp(`${testId}-scroll`)}
        >
          {list.isPending ? (
            <div className="grid grid-cols-2 gap-12 sm:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="aspect-[16/11] w-full rounded-lg" />
              ))}
            </div>
          ) : list.isError && items.length === 0 ? (
            <div className="flex flex-col items-start gap-10 py-24">
              <p className="font-sans text-s14 text-text-secondary">{C.loadFailed}</p>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void list.refetch()}
                testId={`${testId}-retry`}
              >
                {C.retry}
              </Button>
            </div>
          ) : items.length === 0 ? (
            <p
              className="py-[40px] text-center font-sans text-s14 text-text-secondary"
              {...testIdProp(`${testId}-empty`)}
            >
              {filtering ? C.noMatch : C.empty}
            </p>
          ) : (
            <div
              role={multiple ? 'group' : 'radiogroup'}
              aria-label={title}
              className="grid grid-cols-2 gap-12 sm:grid-cols-3"
              {...testIdProp(`${testId}-grid`)}
            >
              {items.map((item) => {
                const selected = chosen.includes(item.id);
                const place = ordered && selected ? chosenItems.indexOf(item) + 1 : null;
                const shut = full && !selected;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role={multiple ? 'checkbox' : 'radio'}
                    aria-checked={selected}
                    aria-disabled={shut || undefined}
                    onClick={() => toggle(item.id)}
                    className={cn(
                      'relative flex min-w-0 flex-col gap-6 rounded-lg border p-6 text-left outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                      selected
                        ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                        : 'border-border-subtle bg-surface hover:border-border',
                      shut && 'cursor-not-allowed opacity-50 hover:border-border-subtle',
                    )}
                    {...testIdProp(`${testId}-item-${item.id}`)}
                  >
                    {item.kind === 'image' ? (
                      <img
                        src={item.url}
                        alt=""
                        className={cn(
                          'w-full rounded-md bg-surface-sunken object-cover',
                          square ? 'aspect-square' : 'aspect-[16/9] object-[50%_30%]',
                        )}
                      />
                    ) : (
                      <span className="flex aspect-[16/9] w-full flex-col items-center justify-center gap-4 rounded-md bg-surface-sunken text-text-secondary">
                        <Icon icon={FileText} size={22} aria-hidden />
                        <span className="font-sans text-s12 font-w600">{C.pdf}</span>
                      </span>
                    )}
                    <span className="truncate px-2 font-sans text-s13 font-w500 text-text-strong">
                      {item.title}
                    </span>
                    {selected && multiple && (
                      <span
                        aria-hidden
                        className={cn(
                          'absolute top-10 flex size-20 items-center justify-center rounded-full bg-brand-600 font-sans text-s12 font-w700 text-on-brand',
                          ordered ? 'left-10' : 'right-10',
                        )}
                        {...testIdProp(place === null ? undefined : `${testId}-place-${item.id}`)}
                      >
                        {place === null ? <Icon icon={Check} size={12} aria-hidden /> : place}
                      </span>
                    )}
                    {!selected && ordered && (
                      <span
                        aria-hidden
                        className="absolute left-10 top-10 size-20 rounded-full border-2 border-surface bg-black/15 shadow-sm ring-1 ring-black/25"
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {list.hasNextPage && (
            <div className="flex justify-center pt-12">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${testId}-more`}
              >
                {C.more}
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-8 border-t border-border-subtle px-24 pb-20 pt-16">
          {multiple && (
            <span
              className="font-sans text-s13 text-text-secondary"
              aria-live="polite"
              {...testIdProp(`${testId}-count`)}
            >
              {max === undefined ? C.count(files, graphics) : C.countOf(chosenItems.length, max)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-8">
            <Button
              appearance="saas"
              tone="secondary"
              onClick={() => close(false)}
              testId={`${testId}-cancel`}
            >
              {C.cancel}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              disabled={chosenItems.length === 0 && !multiple}
              loading={applying}
              onClick={() => onChoose(chosenItems)}
              testId={`${testId}-choose`}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
